import React, { useCallback, useMemo } from "react";
import { Box, Flex, Select, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ConditionBuilder } from "components/ConditionBuilder";
import { createDefaultConditionGroup } from "components/ConditionBuilder/utils/conditionUtils";
import {
  EnvironmentalVariablesLoadingSkeleton,
  EnvironmentalVariablesErrorState,
} from "components/ConditionBuilder/components";
import { ConditionsEmptyState } from "pages/Event/components/ConditionsEmptyState";
import { ConditionGroup } from "stores/data/insightAlerts/insightAlertModel";
import { IEnvironmentalVariable } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { ISensorMeasuredData } from "types/ISensorMeasuredData";
import { SensorSelector } from "./SensorSelector";
import { SA_COLORS } from "./sensor-alert-colors";
import { TogglePillButton } from "./TogglePillButton";

/** Frequency options in minutes (1h–24h) */
const FREQUENCY_OPTIONS = Array.from({ length: 24 }, (_, i) => (i + 1) * 60);

interface SensorConfigurationProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  sensorConditions: ConditionGroup[];
  alertFrequency: number;
  environmentalVariables?: IEnvironmentalVariable[];
  isLoadingEnvVariables: boolean;
  hasErrorLoadingEnvVariables: boolean;
  onRetryLoadEnvVariables: () => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  selectedSensors: ISensorMeasuredData[];
  errors?: any;
  touched?: any;
}

/** Filter environmental variables to only accumulated precipitation ones */
function filterAccumulatedPrecipitationVariables(
  variables?: IEnvironmentalVariable[],
): IEnvironmentalVariable[] {
  if (!variables) return [];
  return variables.filter((v) => {
    const name = v.internal_name?.toLowerCase() || v.name?.toLowerCase() || "";
    return name.includes("precip_accum") || name.includes("precip_accumulated");
  });
}

export const SensorConfiguration: React.FC<SensorConfigurationProps> = ({
  t,
  sensorConditions,
  alertFrequency,
  environmentalVariables,
  isLoadingEnvVariables,
  hasErrorLoadingEnvVariables,
  onRetryLoadEnvVariables,
  setFieldValue,
  selectedSensors,
  errors,
  touched,
}) => {
  // Use insightAlert t for ConditionBuilder (it expects keys like "and", "or", "isGreaterThan" etc.)
  const { t: tConditionBuilder } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightAlert",
  });

  const precipitationVariables = useMemo(
    () => filterAccumulatedPrecipitationVariables(environmentalVariables),
    [environmentalVariables],
  );

  const hasConditions = sensorConditions && sensorConditions.length > 0;

  const handleCreateCondition = useCallback(() => {
    const firstPrecipVar = precipitationVariables[0];
    const defaultGroup = firstPrecipVar
      ? createDefaultConditionGroup(
          null,
          firstPrecipVar.id,
          firstPrecipVar.internal_name,
        )
      : createDefaultConditionGroup();
    setFieldValue("sensorAlertConfig.conditions", [defaultGroup], false);
  }, [precipitationVariables, setFieldValue]);

  // Periodicity state
  const isRecurring = alertFrequency !== -1;

  const handleSelectRecurring = useCallback(() => {
    if (!isRecurring) {
      setFieldValue("sensorAlertConfig.alertFrequency", 60, false);
    }
  }, [isRecurring, setFieldValue]);

  const handleSelectSingle = useCallback(() => {
    if (isRecurring) {
      setFieldValue("sensorAlertConfig.alertFrequency", -1, false);
    }
  }, [isRecurring, setFieldValue]);

  const handleFrequencyChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFieldValue(
        "sensorAlertConfig.alertFrequency",
        Number(e.target.value),
        false,
      );
    },
    [setFieldValue],
  );

  return (
    <Stack spacing="16px">
      {/* Sensor selection summary (count + names tooltip). Editing is handled
          by the gateway button rendered above this section. */}
      <SensorSelector t={t} selectedSensors={selectedSensors} />

      {/* Condition Builder */}
      {isLoadingEnvVariables ? (
        <EnvironmentalVariablesLoadingSkeleton />
      ) : hasErrorLoadingEnvVariables ? (
        <EnvironmentalVariablesErrorState
          errorMessage={t("errorLoadingSensorEnvironmentalVariables")}
          retryLabel={t("retry")}
          onRetry={onRetryLoadEnvVariables}
        />
      ) : !hasConditions ? (
        <ConditionsEmptyState
          onCreateCondition={handleCreateCondition}
          t={(key) => {
            if (key === "conditionsEmptyStateTitle")
              return t("sensorConditionsEmptyStateTitle");
            if (key === "conditionsEmptyStateCreateButton")
              return t("sensorConditionsEmptyStateCreateButton");
            return t(key);
          }}
          showRiskSuggestion={false}
        />
      ) : (
        <Box bg="#F4F4F5" padding="24px" borderRadius={8}>
          <ConditionBuilder
            conditionGroups={sensorConditions}
            environmentalVariables={precipitationVariables}
            setFieldValue={setFieldValue}
            t={tConditionBuilder}
            errors={errors}
            fieldArrayName="sensorAlertConfig.conditions"
            defaultEnvironmentalVariableId={precipitationVariables[0]?.id}
          />
        </Box>
      )}

      {/* Error message when no conditions are added */}
      {!!(errors?.sensorAlertConfig && touched?.sensorAlertConfig) && (
        <Text color="red.500" fontSize="sm">
          {t("pleaseAddSensorCondition")}
        </Text>
      )}

      {/* Alert Periodicity (FR-046 to FR-051) */}
      <Box
        border="1px solid"
        borderColor={SA_COLORS.panelBorder}
        borderRadius="8px"
        padding="16px"
        display="flex"
        flexDirection="column"
        gap="24px"
      >
        {/* Title + Toggle buttons */}
        <Flex direction="column" gap="16px">
          <Text
            fontWeight="500"
            fontSize="14px"
            lineHeight="1.71"
            color={SA_COLORS.textTitle}
          >
            {t("periodicityLabel")}
          </Text>

          <Flex gap="16px">
            <TogglePillButton
              active={isRecurring}
              onClick={handleSelectRecurring}
              label={t("periodicityRecurring")}
              tooltip={
                <Box>
                  <Text fontWeight="600" mb="8px" color={SA_COLORS.textTitle}>
                    {t("recurringTooltipTitle")}
                  </Text>
                  <Text color={SA_COLORS.textBody} fontWeight="400">
                    {t("recurringTooltip")}
                  </Text>
                </Box>
              }
            />
            <TogglePillButton
              active={!isRecurring}
              onClick={handleSelectSingle}
              label={t("periodicitySingle")}
              tooltip={
                <Box>
                  <Text fontWeight="600" mb="8px" color={SA_COLORS.textTitle}>
                    {t("singleTooltipTitle")}
                  </Text>
                  <Text color={SA_COLORS.textBody} fontWeight="400">
                    {t("singleTooltip")}
                  </Text>
                </Box>
              }
            />
          </Flex>
        </Flex>

        {/* Description block — conditional on selected periodicity */}
        {isRecurring ? (
          <Flex justify="space-between" align="center" gap="10px">
            <Box flex="1">
              <Text
                fontWeight="600"
                fontSize="14px"
                lineHeight="1.21"
                color={SA_COLORS.textTitle}
                mb="16px"
              >
                {t("periodicityRecurring")}
              </Text>
              <Text
                fontWeight="400"
                fontSize="14px"
                lineHeight="1.21"
                color={SA_COLORS.textBody}
              >
                {t("recurringDescription")}
              </Text>
            </Box>

            <Box flex="1">
              <Text
                fontWeight="400"
                fontSize="14px"
                lineHeight="1.43"
                color={SA_COLORS.textFormLabel}
                mb="6px"
              >
                {t("frequencyLabel")}
              </Text>
              <Select
                value={alertFrequency}
                onChange={handleFrequencyChange}
                bg="white"
                borderColor="#D5D7DA"
                borderRadius="8px"
                _hover={{ borderColor: "#A1A1AA" }}
                _focus={{
                  borderColor: SA_COLORS.primary,
                  boxShadow: `0 0 0 1px ${SA_COLORS.primary}`,
                }}
              >
                {FREQUENCY_OPTIONS.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {t("frequencyHour", { count: minutes / 60 })}
                  </option>
                ))}
              </Select>
            </Box>
          </Flex>
        ) : (
          <Box>
            <Text
              fontWeight="600"
              fontSize="14px"
              lineHeight="1.21"
              color={SA_COLORS.textTitle}
              mb="16px"
            >
              {t("periodicitySingle")}
            </Text>
            <Text
              fontWeight="400"
              fontSize="14px"
              lineHeight="1.21"
              color={SA_COLORS.textBody}
            >
              {t("singleDescription")}
            </Text>
          </Box>
        )}
      </Box>
    </Stack>
  );
};
